import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { useHistory } from 'react-router-dom';
import QRCode from 'qrcode.react';
import classNames from 'classnames';

import dayjs from 'dayjs';
import { CONTEXT, STORE_3G_HOST, STORE_3G_CHANNEL } from '../../config';
import { getAAppDetails, showAAppQRCode, getAAppGift } from '../../actions/adetail';
import { setTopImgVideoVisible } from '../../actions/layout';
import { installAndroidSoft } from '../../actions/anative';
import { logging, logPostInfo } from '../../actions/log';
import Loading from '../../components/Loading/Loading';
import { getQueryString, scrollingDom } from '../../utils/utils';
import styles from './SoftDetail.module.less';
import SoftDetailMore from './SoftDetailMore';
import SoftDetailSimple from './SoftDetailSimple';
import SoftRecommend from './SoftRecommend';
import SoftDetailSwiper from './SoftDetailSwiper';
import SoftDetailInfos from './SoftDetailInfos';
import SoftDetailGift from './SoftDetailGift';

import DetailScore from '../../components/Detail/DetailScore';
import DetailComment from '../../components/Detail/DetailComment';
import DetailCommentList from '../../components/Detail/DetailCommentList';

const selectAppDetails = createSelector(
  state => state.adetail,
  adetail => adetail.appDetails,
);

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

const selectDownloadSofts = createSelector(
  state => state.download,
  download => download.softs,
);

const selectCommentList = createSelector(
  state => state.comment,
  comment => comment.commentList,
);

const selectUpgradeSofts = createSelector(
  state => state.native,
  native => native.upgradeSofts,
);

const selectDetailGift = createSelector(
  state => state.adetail,
  adetail => adetail.detailGift,
);
const selectTopImgVideoVisible = createSelector(
  state => state.layout,
  layout => layout.topImgVideo,
);

export default memo(props => {
  const { match } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  const logInfoData = useSelector(selectLogInfo);
  const appDetailsData = useSelector(selectAppDetails); // 详情数据
  const commentList = useSelector(selectCommentList); // 只为拿取评分数目
  const detailGift = useSelector(selectDetailGift); // 只为拿取礼包数
  const upgradeSofts = useSelector(selectUpgradeSofts); // 可更新
  const topImgVideo = useSelector(selectTopImgVideoVisible); // 头部图片视频

  const [logFlag, setLogFlag] = useState(false); // 只报一次的桩
  const detailVideo = useRef(null);

  const [showContent, setShowContent] = useState('detail'); // 展示内容
  const [isPlay, setIsPlay] = useState(true); // 视频是否正在播放

  /**
   * 统计页面浏览时长
   */
  useEffect(() => {
    const initTime = +new Date();
    return () => {
      const leaveTime = +new Date();
      dispatch(
        logging({
          category: 'I',
          action: 'View_Time',
          label: '',
          param2: {
            source: `AppDetail`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            initTime,
            leaveTime,
            time: leaveTime - initTime,
            packageName: match.params.pn,
            versionCode: match.params.vc,
          },
          param3: match.params.bizInfo,
        }),
      );
    };
  }, [match]);

  useEffect(() => {
    // 获取软件数据
    if (!appDetailsData || (appDetailsData && appDetailsData.paramPN !== match.params.pn)) {
      dispatch(
        getAAppDetails({
          pn: match.params.pn || '',
          vc: match.params.vc || 0,
          bizInfo: match.params.bizInfo || {},
        }),
      );
      return;
    }

    // 软件已经下架, 无效软件，后端未知已经下架
    if (
      appDetailsData &&
      appDetailsData.paramPN === match.params.pn &&
      (appDetailsData.status === 500 || appDetailsData.status === -1) &&
      appDetailsData.loading === false
    ) {
      history.replace(`${CONTEXT}/error?sourcePage=ASoftDetail`);
    }
  }, [dispatch, appDetailsData, match]);

  // 获取礼包
  useEffect(() => {
    dispatch(
      getAAppGift({
        androidType: '1',
        softInfo: match.params.pn || '',
        bizInfo: match.params.bizInfo || {},
      }),
    );
  }, [match]);

  /**
   * 进入软件页面并且开始下载, 等待更新列表、静默安装数据获取完成才开始下载
   */
  useEffect(() => {
    // 软件数据获取成功处理
    const isDownload = getQueryString('isDownload');
    const adetailIsDownload = sessionStorage.getItem('adetailIsDownload'); // 用于第一次进入页面就下载软件
    if (
      appDetailsData &&
      appDetailsData.softID &&
      match.params.pn === appDetailsData.paramPN &&
      upgradeSofts &&
      upgradeSofts.length != undefined &&
      (!adetailIsDownload || adetailIsDownload !== appDetailsData.paramPN)
    ) {
      logPV(match.params.pn, match.params.bizInfo);
      // 如果URL上有isDownload=1, 则下载应用。(如果下载状态有(1,2,3,4,5,6) 则当做已经下载过了的就不再下载)
      const status = parseInt(appDetailsData.status, 10) || 0;
      if (status === 0 && isDownload === '1') {
        downLoadInstallSoft(appDetailsData, match.params.bizInfo, upgradeSofts);
        sessionStorage.setItem('adetailIsDownload', appDetailsData.paramPN);
      }
    }
  }, [dispatch, appDetailsData, upgradeSofts]);

  // 进入详情页面直接下载
  const downLoadInstallSoft = useCallback(
    (softInfo, bizInfo, upgrade_softs) => {
      const upgrade = upgrade_softs.find(
        soft_ =>
          (soft_.androidType !== '1' &&
            (soft_.softID === softInfo.softID || soft_.SoftID === softInfo.softID)) ||
          (soft_.androidType === '1' && soft_.packageName === softInfo.packageName),
      );
      const logParam2 = {
        source: 'AppDetail',
        source_p: logInfoData.source_p,
        source_pp: logInfoData.source_pp,
        softid: softInfo.packageName,
        softName: softInfo.softName,
        packageName: softInfo.packageName,
        versionCode: softInfo.versionCode,
        softType: '1',
        type: upgrade ? 'u' : 'd',
        url: softInfo.downLoadUrl || softInfo.downloadUrl,
      };
      const installSoftInfo = { ...softInfo, logInfo: logParam2, bizInfo };
      dispatch(installAndroidSoft(installSoftInfo));
    },
    [dispatch],
  );

  const logPV = useCallback(
    (softID, bizInfo) => {
      if (logFlag === softID) {
        return;
      }
      setLogFlag(softID);
      // 推荐页面PV打桩  必须设备信息获取成功之后进行
      const param2 = {
        source: 'AppDetail',
        source_p: logInfoData.source_p,
        source_pp: logInfoData.source_pp,
        softid: softID,
        softType: '0',
      };
      // 搜索结果也跳转过来的，带上附加信息
      if (logInfoData.source === 'Search' || logInfoData.source_p === 'Search') {
        param2.search_type = logInfoData.search_type;
        param2.search_word = logInfoData.search_word;
      }
      dispatch(
        logging({
          category: 'P',
          action: 'AppDetail',
          label: '',
          param2,
          param3: bizInfo,
        }),
      );
      dispatch(
        logPostInfo({
          source: 'AppDetail',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
        }),
      );
    },
    [dispatch, logFlag],
  );

  // 切换详情和评论菜单
  const handleChangeMenu = useCallback(
    menu => {
      if (menu) {
        dispatch(
          logging({
            category: 'C',
            action: 'AppDetail_Tab',
            label: '',
            param2: {
              source: 'AppDetail',
              source_p: logInfoData.source_p,
              source_pp: logInfoData.source_pp,
              softid: appDetailsData.softID,
              softType: '1',
              tab: menu,
            },
            param3: appDetailsData.bizInfo,
          }),
        );
        setShowContent(menu || 'detail');
      }
    },
    [appDetailsData],
  );

  /**
   * 点击控制播放音乐
   */
  const onVideoClick = useCallback((play, mute) => {
    if (window.TopVideoPlayPause) {
      window.TopVideoPlayPause(!play, mute);
      setIsPlay(!play);
    }
  });

  /**
   * 已进入页面先取消展示视频，避免有视频
   */
  useEffect(() => {
    dispatch(
      setTopImgVideoVisible({
        visible: false,
        isScrolled: false,
      }),
    );
  }, [appDetailsData]);

  /**
   * 退出页面时不展示视频
   */
  useEffect(() => {
    return () => {
      dispatch(
        setTopImgVideoVisible({
          visible: false,
          isScrolled: false,
        }),
      );
    };
  }, [appDetailsData]);

  /**
   * 处理退回页面，调整显示视频
   */
  useEffect(() => {
    if (
      appDetailsData &&
      appDetailsData.paramPN === match.params.pn &&
      appDetailsData.cover &&
      history.action === 'POP'
    ) {
      if (topImgVideo && topImgVideo.visible) {
        const imgVideoDom = document.getElementById('img_video_detail');
        // 根据进度设置视频的透明度
        if (imgVideoDom && imgVideoDom.style) {
          imgVideoDom.style.opacity = 1;
        }
      } else {
        dispatch(
          setTopImgVideoVisible({
            visible: true,
            page: 'detail',
            data: appDetailsData.cover,
          }),
        );
      }
    }
  }, [match, appDetailsData, topImgVideo]);

  /**
   * 非回退进入详情页面，调整距离显示视频
   */
  useEffect(() => {
    if (
      appDetailsData &&
      appDetailsData.paramPN === match.params.pn &&
      appDetailsData.cover &&
      history.action !== 'POP'
    ) {
      setTimeout(() => {
        dispatch(
          setTopImgVideoVisible({
            visible: true,
            page: 'detail',
            data: appDetailsData.cover,
          }),
        );
        const imgVideoDom = document.getElementById('img_video_detail');
        if (imgVideoDom && imgVideoDom.style) {
          imgVideoDom.style.opacity = 1;
        }
      }, 100);
    }
  }, [match, appDetailsData, history]);

  // useEffect(() => {
  //   if (
  //     appDetailsData &&
  //     appDetailsData.cover &&
  //     detailVideo &&
  //     detailVideo.current &&
  //     history.action === 'POP'
  //   ) {
  //     if (topImgVideo && topImgVideo.visible) {
  //       const videoDom = detailVideo.current;
  //       const imgVideoDom = document.getElementById('img_video_detail');
  //       // 根据进度设置其蒙面的高度
  //       if (videoDom && videoDom.style) {
  //         videoDom.style.height = '326px';
  //       }
  //       // 根据进度设置视频的透明度
  //       if (imgVideoDom && imgVideoDom.style) {
  //         imgVideoDom.style.opacity = 1;
  //       }
  //     } else {
  //       dispatch(
  //         setTopImgVideoVisible({
  //           visible: true,
  //           page: 'detail',
  //           data: appDetailsData.cover,
  //         }),
  //       );
  //     }
  //   }
  // }, [appDetailsData, detailVideo, topImgVideo]);
  // useEffect(() => {
  //   if (
  //     appDetailsData &&
  //     appDetailsData.cover &&
  //     detailVideo &&
  //     detailVideo.current &&
  //     history.action !== 'POP'
  //   ) {
  //     // 为了解决重复进入同一个详情页面，会有 contDom.scrollTop !== 0 的情况
  //     setTimeout(() => {
  //       const videoDom = detailVideo.current;
  //       const contDom = document.getElementById('container_content');
  //       if (contDom.scrollTop === 0) {
  //         let isSetTopImgVideoVisible = false;
  //         const moreDetailToTop = 326; //可以下滑的高度 400-74
  //         const speed = 4; // 下滑速度

  //         function addHeight() {
  //           const imgVideoDom = document.getElementById('img_video_detail');
  //           setTimeout(() => {
  //             const height = parseInt(videoDom.style.height || 0);
  //             const toHeight = height + speed;
  //             // 根据进度设置其蒙面的高度
  //             if (videoDom && videoDom.style) {
  //               videoDom.style.height =
  //                 (toHeight > moreDetailToTop ? moreDetailToTop : toHeight) + 'px';
  //             }
  //             // 根据进度设置视频的透明度
  //             if (imgVideoDom && imgVideoDom.style) {
  //               imgVideoDom.style.opacity =
  //                 toHeight / moreDetailToTop > 1 ? 1 : toHeight / moreDetailToTop;
  //             }
  //             // 高度大于0 则设置显示视频
  //             if (height > 0 && !isSetTopImgVideoVisible) {
  //               dispatch(
  //                 setTopImgVideoVisible({
  //                   visible: true,
  //                   page: 'detail',
  //                   data: appDetailsData.cover,
  //                 }),
  //               );
  //               isSetTopImgVideoVisible = true;
  //             }
  //             if (height < moreDetailToTop) {
  //               addHeight();
  //             }
  //           }, 10);
  //         }
  //         addHeight();
  //       }
  //     }, 100);
  //   }
  // }, [appDetailsData, detailVideo, history]);

  if (
    !appDetailsData ||
    !appDetailsData.packageName ||
    match.params.pn !== appDetailsData.packageName
  ) {
    return (
      <div className={styles.loading}>
        <Loading />
      </div>
    );
  }

  return (
    <>
      <div className={styles.detail}>
        {appDetailsData && appDetailsData.cover && (
          <div
            className={styles.detail_video}
            ref={detailVideo}
            // style={{ height: history.action === 'POP' ? '326px' : '0px' }}
            onClick={() => {
              // onVideoClick(isPlay, false);
            }}
          >
            {appDetailsData.cover && appDetailsData.cover.showVideo === '1' && (
              <div
                className={`iconfont ${isPlay ? 'icon-v_play' : 'icon-v_stop'} ${
                  styles.detail_video_btn
                }`}
                onClick={e => {
                  onVideoClick(isPlay, true);
                  e.stopPropagation();
                }}
              />
            )}
          </div>
        )}
        <div className={styles.detail_box}>
          <div className={styles.detail_more} id="soft_detail_more">
            <SoftDetailMore softDetails={appDetailsData} match={match} />
          </div>
          <div className={styles.detail_simple} id="soft_detail_simple">
            <SoftDetailSimple softDetails={appDetailsData} match={match} />
            <div className={styles.detail_menu}>
              <div
                className={
                  showContent === 'detail' ? 'detail_menu_item_active' : 'detail_menu_item'
                }
                onMouseOver={() => {
                  handleChangeMenu('detail');
                }}
                onFocus={() => undefined}
              >
                详情
              </div>
              <div
                className={
                  showContent === 'comment' ? 'detail_menu_item_active' : 'detail_menu_item'
                }
                onMouseOver={() => {
                  handleChangeMenu('comment');
                }}
                onFocus={() => undefined}
              >
                评论
                {commentList &&
                  commentList.dataList &&
                  commentList.dataList.length > 0 &&
                  `(${commentList.totalCount})`}
              </div>
              {detailGift &&
                detailGift.softInfo === match.params.pn &&
                detailGift.dataList &&
                detailGift.dataList.length > 0 && (
                  <div
                    className={
                      showContent === 'gift' ? 'detail_menu_item_active' : 'detail_menu_item'
                    }
                    onMouseOver={() => {
                      handleChangeMenu('gift');
                    }}
                    onFocus={() => undefined}
                  >
                    <span className="menu_gift" />
                    礼包
                  </div>
                )}
            </div>
          </div>
          <div className={styles.detail_menu} id="soft_detail_menu">
            <div
              className={showContent === 'detail' ? 'detail_menu_item_active' : 'detail_menu_item'}
              onMouseOver={() => {
                handleChangeMenu('detail');
              }}
              onFocus={() => undefined}
            >
              详情
            </div>
            <div
              className={showContent === 'comment' ? 'detail_menu_item_active' : 'detail_menu_item'}
              onMouseOver={() => {
                handleChangeMenu('comment');
              }}
              onFocus={() => undefined}
            >
              评论
              {commentList &&
                commentList.dataList &&
                commentList.dataList.length > 0 &&
                `(${commentList.totalCount})`}
            </div>
            {detailGift &&
              detailGift.softInfo === match.params.pn &&
              detailGift.dataList &&
              detailGift.dataList.length > 0 && (
                <div
                  className={
                    showContent === 'gift' ? 'detail_menu_item_active' : 'detail_menu_item'
                  }
                  onMouseOver={() => {
                    handleChangeMenu('gift');
                  }}
                  onFocus={() => undefined}
                >
                  <span className="menu_gift" />
                  礼包
                </div>
              )}
          </div>
          <div
            className={styles.detail_content}
            style={{ display: showContent === 'detail' ? 'flex' : 'none' }}
          >
            <div className={styles.detail_pic_view}>
              <SoftDetailSwiper
                swiperData={appDetailsData.captureFileList}
                appDetailsData={appDetailsData}
              />
            </div>
            <SoftDetailInfos appDetailsData={appDetailsData} />
            <div className={styles.detail_recommend}>
              <SoftRecommend softDetails={appDetailsData} />
            </div>
          </div>

          <div
            className={styles.detail_comment}
            style={{ display: showContent === 'comment' ? 'flex' : 'none' }}
          >
            <DetailScore appDetailsData={appDetailsData} />
            <DetailComment appDetailsData={appDetailsData} />
            <DetailCommentList appDetailsData={appDetailsData} />
          </div>
          {detailGift &&
            detailGift.softInfo === match.params.pn &&
            detailGift.dataList &&
            detailGift.dataList.length > 0 && (
              <div
                className={styles.detail_gift}
                style={{ display: showContent === 'gift' ? 'flex' : 'none' }}
              >
                <SoftDetailGift props={props} appDetailsData={appDetailsData} />
                <div className={styles.detail_recommend}>
                  <SoftRecommend softDetails={appDetailsData} />
                </div>
              </div>
            )}
        </div>
      </div>
    </>
  );
});
